import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserListDto, UserResponse, UserFormData, Supervisor, User } from './users.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private basePath = 'users';

  findAll(filters: UserListDto): Observable<UserResponse> {
    let params = new HttpParams();

    if (filters.page !== undefined) {
      params = params.set('page', filters.page.toString());
    }
    if (filters.limit !== undefined) {
      params = params.set('limit', filters.limit.toString());
    }
    if (filters.search) {
      params = params.set('search', filters.search);
    }
    if (filters.role) {
      params = params.set('role', filters.role);
    }
    if (filters.status) {
      params = params.set('status', filters.status);
    }

    return this.http.get<UserResponse>(`/${this.basePath}`, { params });
  }

  createUser(data: UserFormData): Observable<User> {
    return this.http.post<User>(`/${this.basePath}`, data);
  }

  /**
   * Fetch users eligible to be a supervisor for the given supervisory level.
   * level param: 'L2' when creating an L1 user, 'L3' when creating an L2 user.
   */
  findSupervisors(level: string): Observable<Supervisor[]> {
    const params = new HttpParams()
      .set('supervisory_level', level)
      .set('limit', '100');

    return this.http.get<UserResponse>(`/${this.basePath}`, { params }).pipe(
      map(res => res.data.map(u => ({
        id: u.id,
        name: u.full_name,
        role: u.role?.name ?? '',
        directReports: 0
      })))
    );
  }

  checkEmailUniqueness(email: string): Observable<{ available: boolean; user?: { full_name: string; id: string } }> {
    return this.http.get<{ available: boolean; user?: { full_name: string; id: string } }>(`/${this.basePath}/check-by-email?email=${email}`);
  }

  checkPhoneUniqueness(phone: string): Observable<{ available: boolean; secondsAgo?: number }> {
    const params = new URLSearchParams({ phone });
    return this.http.get<{ available: boolean; secondsAgo?: number }>(`/${this.basePath}/check-by-phone?${params.toString()}`);
  }

  deactivate(id: string): Observable<User> {
    return this.http.patch<User>(`/${this.basePath}/${id}/deactivate`, {});
  }

  activate(id: string): Observable<User> {
    return this.http.patch<User>(`/${this.basePath}/${id}/activate`, {});
  }

  getUserById(id: string): Observable<UserFormData> {
    return this.http.get<UserFormData>(`/${this.basePath}/${id}`)
  }

  updateUser(id: string, changes: Partial<UserFormData>): Observable<UserFormData> {
    return this.http.patch<UserFormData>(`/${this.basePath}/${id}`, changes);
  }

}
